// Through the barrel, for the same reason RangeOverride.ts does it: anything
// with PanelRange behind it has to come through '.' or this module becomes the
// entry point of that cycle.
import { BloodworkRange } from '.';
import {
  RANGE_BOUNDS,
  RANGE_BOUND_LABELS,
  RangeBound,
} from './RangeCatalog';
import { RangeOverride } from './RangeOverride';
import {
  UnitSystem,
  toCanonicalValue,
  toDisplayValue,
} from './units/unitConversions';
import { MathUtils } from '../utils/MathUtils';

/**
 * What the practitioner has typed into one population row, by bound, exactly
 * as typed. Strings rather than numbers so a half-typed "1." or an emptied
 * field is the editor's problem to describe rather than something that has
 * already been coerced to a number or to NaN.
 */
export type RangeDraft = Partial<Record<RangeBound, string>>;

/** One number that moved without the practitioner touching it. */
export interface RangeKnockOn {
  bound: RangeBound;
  from?: number;
  to?: number;
}

export interface RangeEditEvaluation {
  /** Nothing typed, or everything typed matches what is already in force. */
  unchanged: boolean;
  /** Bounds the practitioner has typed a new value into. */
  changedBounds: RangeBound[];
  /**
   * Blocking. A collapsed band is not untidy, it is a division by zero in the
   * chart coordinate math (see my-ranges-spec.md §5.2), so the editor refuses
   * the save rather than warning about it.
   */
  errors: string[];
  /** Not blocking. The practitioner's clinical judgment, named out loud. */
  warnings: string[];
  /** Derived bounds that moved because a lab bound did. */
  knockOns: RangeKnockOn[];
  /** Every bound as it would resolve after the save, for the dialog. */
  resolved: Partial<Record<RangeBound, number>>;
  /** What LabSmarts resolves the same row to. */
  labSmarts: Partial<Record<RangeBound, number>>;
  /**
   * The override to store, or undefined when the draft leaves nothing to
   * store — which is what typing LabSmarts' own numbers back in means.
   */
  override?: RangeOverride;
}

function labelOf(bound: RangeBound): string {
  return RANGE_BOUND_LABELS[bound];
}

/** A number as the editor prints it: the practitioner's units, rounded. */
function display(
  attribute: string,
  canonical: number | undefined,
  unitSystem: UnitSystem,
  precision: number,
): number | undefined {
  const shown = toDisplayValue(attribute, canonical, unitSystem);
  return shown == null ? undefined : MathUtils.round(shown, precision);
}

/** Pairs that must ascend, in bar-graph order. */
const ORDERED_PAIRS: [RangeBound, RangeBound][] = [
  ['alarmMin', 'labMin'],
  ['labMin', 'optimalMin'],
  ['optimalMin', 'optimalMax'],
  ['optimalMax', 'labMax'],
  ['labMax', 'alarmMax'],
];

function violations(row: Partial<Record<RangeBound, number>>): Set<string> {
  const broken = new Set<string>();
  ORDERED_PAIRS.forEach(([lower, upper]) => {
    const low = row[lower];
    const high = row[upper];
    if (low == null || high == null) {
      return;
    }
    if (!(low < high)) {
      broken.add(`${lower}<${upper}`);
    }
  });
  return broken;
}

/**
 * Work out what a draft edit to one population row would do.
 *
 * Everything the save dialog says comes from here, so the sentence the
 * practitioner confirms and the numbers that get stored cannot disagree: the
 * resolved values, the knock-on a derived bound takes when a lab bound moves,
 * the warnings, and the override itself.
 *
 * `storedRow` is LabSmarts' row as stored — pre-init(), canonical US. The
 * override goes on a COPY of it and init() runs afterwards, which is the same
 * order BloodworkEntryStore.effectiveRanges applies in; anything else and the
 * dialog would predict numbers the reports do not use.
 */
export function evaluateRangeEdit(args: {
  attribute: string;
  storedRow: BloodworkRange;
  existing?: RangeOverride;
  draft: RangeDraft;
  scopeKey: string;
  unitSystem: UnitSystem;
  precision: number;
}): RangeEditEvaluation {
  const { attribute, storedRow, existing, draft, scopeKey, unitSystem, precision } = args;
  const shown = (canonical: number | undefined) =>
    display(attribute, canonical, unitSystem, precision);

  const labSmartsRow = new BloodworkRange(storedRow).init();
  const labSmarts: Partial<Record<RangeBound, number>> = {};
  RANGE_BOUNDS.forEach(bound => {
    labSmarts[bound] = shown(labSmartsRow[bound]);
  });

  // In force before this edit: the existing override over LabSmarts' row.
  const beforeRow = new BloodworkRange(storedRow);
  RANGE_BOUNDS.forEach(bound => {
    if (existing && existing[bound] != null) {
      beforeRow[bound] = existing[bound];
    }
  });
  beforeRow.init();
  const before: Partial<Record<RangeBound, number>> = {};
  RANGE_BOUNDS.forEach(bound => {
    before[bound] = shown(beforeRow[bound]);
  });

  const errors: string[] = [];
  const changedBounds: RangeBound[] = [];
  const next = new RangeOverride({
    attribute,
    scopeKey,
    sourceRangeId: (storedRow as any).id,
    updatedDate: new Date().toISOString(),
  });
  // Start from what is already stored, so editing one bound does not throw
  // away the other five the practitioner set earlier.
  RANGE_BOUNDS.forEach(bound => {
    if (existing && existing[bound] != null) {
      next[bound] = existing[bound];
    }
  });

  RANGE_BOUNDS.forEach(bound => {
    const typed = draft[bound];
    if (typed === undefined) {
      return;
    }
    const text = String(typed).trim();
    if (text === '') {
      // Not treated as "go back to LabSmarts": that is what Reset says
      // plainly, and a number silently vanishing out of a report's grading is
      // too consequential to infer from an empty box.
      errors.push(
        `${labelOf(bound)} needs a number. Use Reset to LabSmarts to go back to LabSmarts' ranges.`,
      );
      return;
    }
    const value = Number(text);
    if (!Number.isFinite(value)) {
      errors.push(`${labelOf(bound)} is not a number.`);
      return;
    }
    const typedDisplay = MathUtils.round(value, precision);
    if (typedDisplay === labSmarts[bound]) {
      // LabSmarts' own number typed back in. Stored as nothing, so this bound
      // follows any correction we make to it later.
      delete next[bound];
      if (before[bound] !== typedDisplay) {
        changedBounds.push(bound);
      }
      return;
    }
    next[bound] = toCanonicalValue(attribute, value, unitSystem);
    if (before[bound] !== typedDisplay) {
      changedBounds.push(bound);
    }
  });

  const stored = RANGE_BOUNDS.filter(bound => next[bound] != null);
  const afterRow = new BloodworkRange(storedRow);
  stored.forEach(bound => {
    afterRow[bound] = next[bound];
  });
  afterRow.init();
  const resolved: Partial<Record<RangeBound, number>> = {};
  RANGE_BOUNDS.forEach(bound => {
    resolved[bound] = shown(afterRow[bound]);
  });

  // Only a violation this edit INTRODUCES blocks the save. A row LabSmarts
  // already stores out of sequence (a derived alarmMin clamped to 0 against a
  // labMin of 0) must not make the rest of that row uneditable.
  const existingViolations = violations(labSmarts);
  violations(resolved).forEach(key => {
    if (existingViolations.has(key)) {
      return;
    }
    const [lower, upper] = key.split('<') as [RangeBound, RangeBound];
    errors.push(
      `${labelOf(lower)} (${resolved[lower]}) must be below ${labelOf(upper)} (${resolved[upper]}).`,
    );
  });

  // A bound the practitioner did not type that moved anyway: init() derives
  // optimal, alarm and stdDev from the lab range, so editing lab moves them.
  const knockOns: RangeKnockOn[] = [];
  RANGE_BOUNDS.forEach(bound => {
    if (next[bound] != null) {
      return;
    }
    if (resolved[bound] !== before[bound]) {
      knockOns.push({ bound, from: before[bound], to: resolved[bound] });
    }
  });

  const warnings: string[] = [];
  const labBand =
    labSmarts.labMin != null && labSmarts.labMax != null
      ? `${labSmarts.labMin} – ${labSmarts.labMax}`
      : undefined;
  if (
    labBand &&
    ((resolved.optimalMin != null && resolved.optimalMin < labSmarts.labMin) ||
      (resolved.optimalMax != null && resolved.optimalMax > labSmarts.labMax))
  ) {
    warnings.push(
      `Optimal ${resolved.optimalMin} – ${resolved.optimalMax} reaches outside LabSmarts' lab range of ${labBand}. Results LabSmarts treats as outside the lab range will read as optimal, and the findings that depend on them will not fire.`,
    );
  }
  const labSmartsSpan =
    labSmarts.optimalMin != null && labSmarts.optimalMax != null
      ? labSmarts.optimalMax - labSmarts.optimalMin
      : undefined;
  const nextSpan =
    resolved.optimalMin != null && resolved.optimalMax != null
      ? resolved.optimalMax - resolved.optimalMin
      : undefined;
  if (
    labSmartsSpan != null &&
    nextSpan != null &&
    labSmartsSpan > 0 &&
    nextSpan > labSmartsSpan * 2
  ) {
    warnings.push(
      `The optimal range is more than twice as wide as LabSmarts' (${labSmarts.optimalMin} – ${labSmarts.optimalMax}), so fewer results will fall outside it.`,
    );
  }

  return {
    unchanged: changedBounds.length === 0,
    changedBounds,
    errors,
    warnings,
    knockOns,
    resolved,
    labSmarts,
    override: stored.length > 0 ? next : undefined,
  };
}

/** "Alarm Max from 184.5 to 147", for the dialog's knock-on line. */
export function knockOnSentence(knockOn: RangeKnockOn): string {
  return `${labelOf(knockOn.bound)} from ${
    knockOn.from == null ? '—' : knockOn.from
  } to ${knockOn.to == null ? '—' : knockOn.to}`;
}
